import apiClient from 'services/api/apiService';
import { getUrlWithQueryParams } from './api/getUrlWithQueryParams';
import { IModelGeneral } from 'types/Model.types';
import { IImageSection } from 'pages/ConfigurePage/FormImages/hooks';
import { ITestCompanyModelConfiguration } from 'types/Configuration.types';
import { IConfigurationQuestion } from 'types/ConfigurationQuestion.types';
import { SortDirection } from 'components/Table/types';
import { cleanPricingQueryData } from 'providers/QueryProvider/helpers';
import { AppMode } from 'store/Common/reducers/common';
import { IPredefinedPricing, IVariables } from 'store/Model/reducers/model';

const BASE_URL = '/v1/models';

export const getModels = async (
  page: number,
  perPage: number,
  searchBy: string,
  categoryId: number | null,
  favorites: boolean,
  lang: string,
  companyId: number | undefined,
  companySellerId?: number | undefined
): Promise<any> => {
  const url = `${BASE_URL}`;
  const queryParamsObj = {
    page: page,
    per_page: perPage,
    search_term: searchBy,
    category_id: categoryId,
    favorites,
    language_code: lang,
    ...(companyId && { company_buyer_id: companyId }),
    ...(companySellerId && { company_seller_id: companySellerId }),
  };
  const fullUrl = getUrlWithQueryParams(url, queryParamsObj);
  const { data } = await apiClient.get<any>(fullUrl);

  return data.data;
};

export interface IQueryResponseDTO {
  id: number;
  general: IModelGeneral;
  images: Record<string, IImageSection>;
  is_using_seller_system_discounts: boolean;
  pricing: Record<string, object>;
  questions: Record<string, object>;
  round_pricing?: boolean;
  version_number: number;
  extra_price_questions?: IConfigurationQuestion[];
  bom_items?: Record<string, object>;
  has_bom_items?: boolean; // Field is present when include_bom_items is false in query request
  variables?: IVariables; // Shown on SpecsPage. Not returned for every model
  conn_lead_mpr: string | null;
  conn_lead_time: number | null;
  currency: string;
}

export const queryModel = async (
  modelId: string,
  queryPayload: object,
  lang: string,
  taxRate?: number,
  testConfiguration?: ITestCompanyModelConfiguration | null,
  editVersionId?: number,
  includeBomItems?: boolean,
  pricing?: Record<string, any>,
  customNetPrice?: number | null | undefined,
  appMode?: AppMode | null,
  predefinedPricing?: IPredefinedPricing | null
): Promise<IQueryResponseDTO> => {
  const cleanedPricing = cleanPricingQueryData(pricing);
  const taxRatePresent = taxRate == 0 || taxRate;
  const url = `${BASE_URL}/${modelId}/query`;
  const { data } = await apiClient.post<any>(url, {
    ...queryPayload,
    language_code: lang,
    ...(taxRatePresent && { tax_rate: taxRate }),
    ...(taxRatePresent &&
      !!testConfiguration && {
        test_company_model_configuration: testConfiguration,
      }),
    ...(editVersionId && { edit_version_id: editVersionId }),
    ...(includeBomItems && { include_bom_items: includeBomItems }),
    ...(cleanedPricing && { pricing: cleanedPricing }),
    ...(customNetPrice !== undefined && {
      custom_total_net_pricing: customNetPrice,
      ...(appMode && { company_mode: appMode }),
    }),
    // Sent only while resolving a quotation line, so the re-query prices it at
    // the rate the quotation was created with instead of today's rate.
    ...(predefinedPricing && {
      predefined_currency: predefinedPricing.currency,
      predefined_exchange_rate: predefinedPricing.exchangeRate,
    }),
  });

  return data.data;
};

export const getDealerPanelModelQuestions = async (
  modelId: number
): Promise<any> => {
  const { data } = await apiClient.get<any>(
    `${BASE_URL}/${modelId}/dealer-panel`
  );
  return data.data;
};

export const getVersions = async (
  id: string,
  page: number,
  perPage: number,
  sortBy: string,
  sortDirection: SortDirection
): Promise<any> => {
  const url = `${BASE_URL}/${id}/versions`;
  const queryParamsObj = {
    page: page,
    per_page: perPage,
    sort_direction: sortDirection,
    sort_by: sortBy,
  };
  const fullUrl = getUrlWithQueryParams(url, queryParamsObj);
  const { data } = await apiClient.get<any>(fullUrl);

  return data.data;
};

export const updateFavoriteModel = async (
  modelId: number,
  isFavorite: boolean
): Promise<any> => {
  const url = `${BASE_URL}/${modelId}/favorite`;
  const { data } = await apiClient.put<any>(url, { is_favorite: isFavorite });
  return data;
};

export const getCustomModels = async (
  page: number,
  perPage: number,
  searchBy: string,
  companyId: number | null,
  archived?: boolean,
  appMode?: AppMode
): Promise<any> => {
  const url = `${BASE_URL}/custom`;
  const queryParamsObj = {
    page: page,
    per_page: perPage,
    search_term: searchBy,
    archived,
    ...(appMode === AppMode.SUPPLIER && { company_seller_id: companyId }),
    ...((appMode === AppMode.BUYER || appMode === AppMode.DEALER_PANEL) && {
      company_buyer_id: companyId,
    }),
  };
  const fullUrl = getUrlWithQueryParams(url, queryParamsObj);
  const { data } = await apiClient.get<any>(fullUrl);

  return data.data;
};
